import Box from '@mui/material/Box';
import React from 'react';

import { sectionGridSx } from './MainPartEdit.helpers';
import { MainPartEditTouched } from './MainPartEdit.props';
import { SizeProps, Tag } from '../../../@types';
import { PartObject, SfhObject } from '../../../@types/part.type';
import { getFleetTagDropDownSelectedLabel, getSFHDropDownSelectedLabel } from '../../../utils/part';
import { Panel } from '../../atoms/Panel';
import { SelectMenu } from '../../atoms/SelectMenu';
import { ComponentMenuItem } from '../../atoms/SelectMenu/SelectMenu.props';
import { TextField } from '../../atoms/TextField';
import { EditFieldRow } from '../../molecules/EditFieldRow';
import { AutocompleteTagType } from '../../organisms/Tags';
import { Tags } from '../../organisms/Tags/Tags';

interface ConfigurationSectionProps {
  value: PartObject;
  sfhDetails: SfhObject;
  isConfigDirty: boolean;
  touched: MainPartEditTouched;
  validate: boolean;
  selectedTags: (string | AutocompleteTagType)[];
  newTags: Tag[];
  allTags: Tag[];
  setSelectedTags: (value: (string | AutocompleteTagType)[]) => void;
  setNewTags: (value: Tag[]) => void;
  onChange: (value: PartObject) => void;
  onTouchReason: () => void;
}

interface ConfigDropdownProps {
  id: string;
  label: string;
  items: ComponentMenuItem[];
  selectedValue: string | number | undefined | null;
  onSelect: (rawValue: string) => void;
}

const ConfigDropdown: React.FC<ConfigDropdownProps> = ({
  id,
  label,
  items,
  selectedValue,
  onSelect,
}) => (
  <EditFieldRow label={label} htmlFor={id}>
    <SelectMenu
      required={false}
      size={SizeProps.Small}
      id={id}
      labelId={`${id}-label`}
      label={label}
      selectedValue={selectedValue || '-1'}
      onChange={onSelect}
      items={items}
      optionalLabelEnabled
    />
  </EditFieldRow>
);

export const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  value,
  sfhDetails,
  isConfigDirty,
  touched,
  validate,
  selectedTags,
  newTags,
  allTags,
  setSelectedTags,
  setNewTags,
  onChange,
  onTouchReason,
}) => {
  const softwares = sfhDetails?.softwares ?? [];
  const hardwares = sfhDetails?.hardwares ?? [];
  const firmwares = sfhDetails?.firmwares ?? [];
  const fleetTags = value?.fleetTag ?? [];

  const reasonError =
    (touched.configReason || validate) && isConfigDirty && !value.configChnageNotes?.trim();

  return (
    <Panel title="Configuration">
      <Box sx={sectionGridSx}>
        {softwares.length > 0 && (
          <ConfigDropdown
            id="part-edit-software"
            label="Software"
            items={softwares}
            selectedValue={value.softwareVersionId}
            onSelect={(raw) =>
              onChange({
                ...value,
                softwareVersionId: Number(raw),
                softwareVersion: getSFHDropDownSelectedLabel(raw, softwares),
              })
            }
          />
        )}
        {hardwares.length > 0 && (
          <ConfigDropdown
            id="part-edit-hardware"
            label="Hardware"
            items={hardwares}
            selectedValue={value.hardwareRevisionId}
            onSelect={(raw) =>
              onChange({
                ...value,
                hardwareRevisionId: Number(raw),
                hardwareRevision: getSFHDropDownSelectedLabel(raw, hardwares),
              })
            }
          />
        )}
        {firmwares.length > 0 && (
          <ConfigDropdown
            id="part-edit-firmware"
            label="Firmware"
            items={firmwares}
            selectedValue={value.firmwareVersionId}
            onSelect={(raw) =>
              onChange({
                ...value,
                firmwareVersionId: Number(raw),
                firmwareVersion: getSFHDropDownSelectedLabel(raw, firmwares),
              })
            }
          />
        )}
        {fleetTags.length > 0 && (
          <ConfigDropdown
            id="part-edit-fleet-tag"
            label="Fleet Tag"
            items={fleetTags}
            selectedValue={value.fleetTagId}
            onSelect={(raw) =>
              onChange({
                ...value,
                fleetTagId: Number(raw),
                fleetTagName: getFleetTagDropDownSelectedLabel(raw, fleetTags),
              })
            }
          />
        )}
        {isConfigDirty && (
          <EditFieldRow
            label="Reason for configuration change"
            required
            fullWidth
            htmlFor="part-edit-config-notes"
            helper={
              reasonError
                ? 'Reason is required when changing configuration'
                : 'Tell us why the configuration is changing — this is recorded against the part history.'
            }
            error={reasonError}
          >
            <TextField
              fullWidth
              multiline
              size={SizeProps.Small}
              rows={3}
              label="Reason for configuration change"
              value={value.configChnageNotes ?? ''}
              error={reasonError}
              inputProps={{ id: 'part-edit-config-notes' }}
              onChange={(configChnageNotes) => onChange({ ...value, configChnageNotes })}
              onBlur={() => onTouchReason()}
            />
          </EditFieldRow>
        )}
        <EditFieldRow label="Tags" fullWidth>
          <Tags
            selectedTags={selectedTags}
            newTags={newTags}
            setSeletedTags={setSelectedTags}
            setNewTags={setNewTags}
            allTags={allTags}
          />
        </EditFieldRow>
        <EditFieldRow label="Notes" fullWidth htmlFor="part-edit-notes">
          <TextField
            fullWidth
            size={SizeProps.Small}
            label="Notes"
            multiline
            rows={4}
            value={value.notes}
            inputProps={{ id: 'part-edit-notes' }}
            onChange={(notes) => onChange({ ...value, notes })}
          />
        </EditFieldRow>
      </Box>
    </Panel>
  );
};
